import { BarChart, type BarChartProps } from "@mantine/charts";
import { Button, Flex, Group, Stack, type FlexProps } from "@mantine/core";
import classNames from "classnames";
import { type DateTime } from "luxon";
import { useMemo, useState } from "react";
import { numericFormatter } from "react-number-format";
import type { TooltipPayload } from "recharts";
import { getDefaultNumericFormatterProps } from "../../../utils/getDefaultNumericFormatterProps";
import { MantineChartLegend } from "../common/MantineChartLegend";
import { getColoredSeries } from "../utils/getColoredSeries";
import "./MantineBarChart.scss";
import { MantineBarChartTooltip } from "./MantineBarChartTooltip";

type MantineBarChartProps = BarChartProps & {
  containerProps?: FlexProps;
  legendPosition?: "top" | "right";
  defaultInactiveSeries?: Array<string>;
  zoomable?: boolean;
  onZoom?: (start: DateTime, end: DateTime) => void;
  onZoomReset?: () => void;
};

function MantineBarChart({
  barProps,
  className,
  containerProps,
  data,
  dataKey,
  getBarColor,
  h = 450,
  series,
  tooltipProps,
  withLegend,
  defaultInactiveSeries = [],
  legendPosition = "right",
  xAxisLabel,
  xAxisProps,
  yAxisProps,
  zoomable,
  onZoom,
  onZoomReset,
  ...props
}: MantineBarChartProps) {
  const [inactiveSeries, setInactiveSeries] = useState<Array<string>>(
    defaultInactiveSeries
  );

  const mutableData = useMemo(
    () => (data ?? []).map((dataEntry) => ({ ...dataEntry })),
    [data]
  );

  const coloredSeries = getColoredSeries(series);

  function handleLegendToggle(seriesId: string) {
    setInactiveSeries((prev) =>
      prev.includes(seriesId)
        ? prev.filter((id) => id !== seriesId)
        : [...prev, seriesId]
    );
  }

  function handleZoom(start: DateTime, end: DateTime) {
    onZoom?.(start, end);
  }

  return (
    <Flex
      align="start"
      className="MantineBarChart"
      direction={legendPosition === "top" ? "column-reverse" : "row"}
      gap={0}
      {...containerProps}
    >
      <Stack
        mt={withLegend && legendPosition === "top" ? "md" : 0}
        w={withLegend && legendPosition === "right" ? "80%" : "100%"}
      >
        {zoomable && (
          <Group>
            <Button
              disabled={!onZoomReset}
              size="compact-xs"
              variant="default"
              onClick={onZoomReset}
            >
              Zoom zurücksetzen
            </Button>
          </Group>
        )}
        <BarChart
          barProps={(series) => ({
            ...barProps,
            hide: inactiveSeries.includes(series.name),
            onClick: (e) => {
              if (zoomable) {
                handleZoom(e.payload.xStart, e.payload.xEnd);
              }
            }
          })}
          className={classNames("MantineBarChart ", className)}
          data={mutableData}
          dataKey={dataKey}
          getBarColor={getBarColor}
          h={h}
          py="lg"
          series={coloredSeries}
          {...props}
          tooltipProps={{
            content: ({
              label,
              payload
            }: {
              label?: string | number;
              payload: TooltipPayload;
            }) => {
              const title =
                label && xAxisProps?.tickFormatter
                  ? xAxisProps.tickFormatter(label, 0)
                  : label;

              return (
                <MantineBarChartTooltip
                  getBarColor={getBarColor}
                  labelFormatter={tooltipProps?.labelFormatter}
                  payload={payload}
                  seriesLabels={coloredSeries.reduce(
                    (result, singleSeries) => ({
                      ...result,
                      [singleSeries.name]:
                        singleSeries.label || singleSeries.name
                    }),
                    {}
                  )}
                  title={String(title)}
                  valueFormatter={(value, name, item, index, payload) =>
                    tooltipProps?.formatter
                      ? tooltipProps.formatter(
                          value,
                          name,
                          item,
                          index,
                          payload
                        )
                      : yAxisProps?.tickFormatter
                        ? yAxisProps.tickFormatter(value, 0)
                        : value.toString()
                  }
                />
              );
            },
            ...tooltipProps
          }}
          withLegend={false}
          xAxisProps={{
            allowDataOverflow: true,
            interval: 0, // -> show all tick labels
            label: { value: xAxisLabel, position: "bottom" },
            ...xAxisProps
          }}
          yAxisProps={{
            tickFormatter: (value) =>
              numericFormatter(
                value.toString(),
                getDefaultNumericFormatterProps()
              ),
            ...yAxisProps
          }}
        />
      </Stack>
      {withLegend && series.length > 0 && (
        <MantineChartLegend
          direction={legendPosition === "top" ? "row" : "column"}
          inactiveSeries={inactiveSeries}
          series={coloredSeries}
          onClick={handleLegendToggle}
        />
      )}
    </Flex>
  );
}

export { MantineBarChart, MantineBarChartProps };
